import React, { Component } from "react";
import {
  PageHeader,
  Row,
  Col,
  Form,
  Table,
  Tag,
  Button,
  Checkbox,
  DatePicker,
  Modal,
  Empty,
  notification,
} from "antd";
import AgentAttendanceService from "../../services/agentattendanceservice";
import { authHeader } from "../../services/auth-header";
import moment from "moment";

/**
 * Ops review of delivery-agent attendance.
 *
 * The verdict is computed server-side (ReviewReasons / NeedsReview); this screen
 * presents it. Two deliberate choices:
 *  - Flagged marks sort FIRST and "only flagged" defaults ON. A reviewer who has
 *    to hunt for problems eventually stops looking.
 *  - The selfie is fetched through the API with the bearer token, never a static
 *    URL. A staff photograph must not be reachable by guessing.
 */
export default class AgentAttendance extends Component {
  constructor(props) {
    super(props);
    this.service = new AgentAttendanceService();
    this.state = {
      fromDate: moment(),
      toDate: moment(),
      onlyFlagged: true,
      rows: [],
      loading: false,
      photoFor: null,
      photoSrc: null,
      photoMsg: "",
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    let res;
    try {
      res = await this.service.getForReview(
      this.state.fromDate ? this.state.fromDate.format("YYYY-MM-DD") : null,
      this.state.toDate ? this.state.toDate.format("YYYY-MM-DD") : null,
      null,
        this.state.onlyFlagged
      );
    } catch (e) {
      res = { message: (e && e.message) || "Request failed" };
    }
    // finally-style: the spinner must never outlive the request, whatever happened.
    if (Array.isArray(res)) {
      this.setState({ rows: res, loading: false });
    } else {
      this.setState({ rows: [], loading: false });
      notification.error({
        message: "Could not load attendance",
        description:
          (res && (res.message || res.statusCode
            ? `${res.message || "Server said"} (status ${res.statusCode || "unknown"})`
            : null)) || "The server did not return a list.",
        duration: 0,
      });
    }
  };

  openPhoto = async (row) => {
    this.setState({ photoFor: row, photoSrc: null, photoMsg: "Loading…" });
    try {
      const res = await fetch(this.service.photoUrl(row.attendanceID), {
        headers: authHeader(),
      });
      if (res.status === 404) {
        // Normal, not an error: photos are recycled after 45 days by design.
        this.setState({ photoMsg: "No photo — not taken, or recycled after 45 days." });
        return;
      }
      if (!res.ok) {
        this.setState({ photoMsg: "Could not load the photo." });
        return;
      }
      const blob = await res.blob();
      this.setState({ photoSrc: URL.createObjectURL(blob), photoMsg: "" });
    } catch (e) {
      this.setState({ photoMsg: "Could not load the photo." });
    }
  };

  closePhoto = () => {
    if (this.state.photoSrc) URL.revokeObjectURL(this.state.photoSrc);
    this.setState({ photoFor: null, photoSrc: null, photoMsg: "" });
  };

  fmt = (v) => (v ? moment(v).format("DD MMM, HH:mm") : "—");

  distance = (m) => {
    if (m === null || m === undefined) return "—";
    return m >= 1000 ? (m / 1000).toFixed(1) + " km" : m + " m";
  };

  columns = [
    {
      title: "Agent",
      dataIndex: "agentName",
      render: (t, r) => (
        <span>
          <strong>{t && t.trim() ? t : "User " + r.userID}</strong>
          <br />
          <small style={{ color: "#888" }}>
            {r.mobileNo || ""} {r.deviceModel ? "· " + r.deviceModel : ""}
          </small>
        </span>
      ),
    },
    {
      title: "In / Out",
      dataIndex: "inTime",
      render: (t, r) => (
        <span>
          {this.fmt(t)}
          <br />
          <small style={{ color: "#888" }}>
            {r.outTime ? this.fmt(r.outTime) : "still on duty"}
          </small>
        </span>
      ),
    },
    {
      title: "Start",
      dataIndex: "startMode",
      render: (t, r) =>
        t === "ENROUTE" ? (
          <span>
            <Tag color="purple">En route</Tag>
            <br />
            <small style={{ color: "#888" }}>claimed {r.claimedPickupID}</small>
          </span>
        ) : (
          <Tag>At store</Tag>
        ),
    },
    {
      title: "Location",
      dataIndex: "locationSource",
      render: (t, r) => (
        <span>
          {t === "GPS" ? <Tag color="green">GPS</Tag> : <Tag color="orange">{t || "—"}</Tag>}
          {r.locationAccuracyM ? (
            <div>
              <small style={{ color: "#888" }}>±{Math.round(r.locationAccuracyM)} m</small>
            </div>
          ) : null}
          {r.isMockLocation ? <Tag color="red">Mock</Tag> : null}
        </span>
      ),
    },
    {
      title: "From store",
      dataIndex: "metresFromStore",
      align: "right",
      render: (m) => this.distance(m),
    },
    {
      title: "Photo",
      dataIndex: "hasPhoto",
      render: (has, r) =>
        has ? (
          <Button size="small" onClick={() => this.openPhoto(r)}>
            View
          </Button>
        ) : r.photoPurgedOn ? (
          <small style={{ color: "#888" }}>Recycled</small>
        ) : (
          <small style={{ color: "#cf1322" }}>None</small>
        ),
    },
    {
      title: "Needs a look",
      dataIndex: "reviewReasons",
      render: (t, r) =>
        r.needsReview ? (
          <span style={{ color: "#cf1322" }}>{t}</span>
        ) : (
          <span style={{ color: "#389e0d" }}>Looks fine</span>
        ),
    },
  ];

  render() {
    const { rows, loading, photoFor, photoSrc, photoMsg } = this.state;
    // Flagged first — a reviewer should meet the problems before the routine days.
    const sorted = [...rows].sort(
      (a, b) => (b.needsReview ? 1 : 0) - (a.needsReview ? 1 : 0)
    );
    const flagged = rows.filter((r) => r.needsReview).length;

    return (
      <div>
        <PageHeader
          title="Agent attendance"
          subTitle="Selfie and location for every delivery agent who marked in"
        />

        <Form layout="vertical" style={{ padding: "0 24px" }}>
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={6}>
              <Form.Item label="From">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={this.state.fromDate}
                  onChange={(d) => this.setState({ fromDate: d })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="To">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={this.state.toDate}
                  onChange={(d) => this.setState({ toDate: d })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label=" " colon={false}>
                <Checkbox
                  checked={this.state.onlyFlagged}
                  onChange={(e) =>
                    this.setState({ onlyFlagged: e.target.checked }, this.load)
                  }
                >
                  Only marks that need a look
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item label=" " colon={false}>
                <Button type="primary" onClick={this.load} loading={loading} block>
                  Show
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <div style={{ padding: "0 24px 8px", color: "#888" }}>
          {!loading && rows.length > 0
            ? `${rows.length} mark${rows.length === 1 ? "" : "s"}` +
              (flagged > 0 ? ` · ${flagged} need a look` : " · nothing flagged")
            : null}
        </div>

        <div style={{ padding: "0 24px 24px" }}>
          <Table
            size="small"
            rowKey="attendanceID"
            loading={loading}
            columns={this.columns}
            dataSource={sorted}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
            rowClassName={(r) => (r.needsReview ? "ant-table-row-flagged" : "")}
            onRow={(r) => ({
              style: r.needsReview ? { background: "#fffbe6" } : {},
            })}
            locale={{
              emptyText: (
                <Empty
                  description={
                    this.state.onlyFlagged
                      ? "Nothing flagged for these dates. Untick the box to see every mark."
                      : "No agent marked in on these dates."
                  }
                />
              ),
            }}
          />
        </div>

        <Modal
          visible={!!photoFor}
          title={
            photoFor
              ? (photoFor.agentName && photoFor.agentName.trim()
                  ? photoFor.agentName
                  : "User " + photoFor.userID) +
                " · " +
                this.fmt(photoFor.inTime)
              : ""
          }
          footer={null}
          onCancel={this.closePhoto}
        >
          <div style={{ textAlign: "center" }}>
            {photoSrc ? (
              <img src={photoSrc} alt="Attendance selfie" style={{ maxWidth: "100%" }} />
            ) : (
              <p style={{ color: "#888" }}>{photoMsg}</p>
            )}
            {photoFor && photoFor.capturedOn ? (
              <p style={{ color: "#888", marginTop: 12, marginBottom: 0 }}>
                <small>
                  Taken {this.fmt(photoFor.capturedOn)} · uploaded{" "}
                  {this.fmt(photoFor.uploadedOn)}
                  {photoFor.uploadDelayMin > 15
                    ? ` (${photoFor.uploadDelayMin} min later)`
                    : ""}
                </small>
              </p>
            ) : null}
          </div>
        </Modal>
      </div>
    );
  }
}
